import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";

import { Annotation } from "@com.mgmtp.a12.base/base-model-api/lib/main/header";
import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { createCustomNotificationAction } from "../../../app/actions";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { reloadActivity, saveActivity } from "../../../utilities/activityUtils";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { getSignStatus, isJsonRpc2ResponseArray } from "../../../utilities/rpcHelper";
import { executeBusy } from "../../../utilities/workflowUtils";
import { PATH_TO_FILE_ATTACHMENT_LOCATION } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { DOWNLOAD_SIGNED_PDF_EVENT } from "../../../utilities/variables/customEventNames";
import { normalizePlatzhalterA12Path } from "../../../utilities/platzhalterHelper";
import { ButtonAction, createEventButtonActionMatcher } from "../../../utilities/actionUtils";
import { StammdatenFormDescriptor } from "../../../utilities/descriptors";
import { screenValidation } from "../../../utilities/validationUtils";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";
import { isRpcErrorResponse } from "../../../utilities/error";

import SingleDocumentData = FormActivity.Data.SingleDocumentData;

/** Error-Key aus {@code SignTransactionExpiredException.ERROR_KEY} im Backend. */
const SIGN_TRANSACTION_EXPIRED_KEY = "sign.transaction.expired";

const logger = LoggerFactory.getLogger("modulf");

/**
 * Reagiert auf den Button „Signiertes Dokument herunterladen" und fragt den
 * Signaturstatus beim DATA Sign Dienst ab. Bevor der Serverstatus abgefragt wird,
 * wird der aktuelle Arbeitsstand gespeichert, damit weder ungespeicherte Änderungen
 * noch die vom Server bei Abschluss (FINISHED) gesetzten Signatur-Daten (Anhang,
 * signiert/signiertAm) durch den anschließenden Reload verloren gehen.
 */
export function* downloadSignedPdfSaga(): SagaGenerator<void> {
	yield* takeLatest(createEventButtonActionMatcher(DOWNLOAD_SIGNED_PDF_EVENT), handle);
}

function* handle(action: ButtonAction): SagaGenerator<void> {
	const activityId = action.payload.activityId;
	const activity = yield* select(ActivitySelectors.activityById(activityId));

	// Stammdaten haben kein Prozess-/Screen-Konzept, gegen das eine vollumfängliche
	// Validierung sinnvoll ausgeführt werden könnte – dort wird ungeprüft heruntergeladen.
	const stammdatenForm = StammdatenFormDescriptor.isInstance(activity?.descriptor);

	yield* call(executeBusy, function* (): SagaGenerator<void> {
		if (!stammdatenForm) {
			const isValid = yield* call(screenValidation, activityId);
			if (!isValid) {
				logger.error("Document is not valid.");
				return;
			}
		}

		yield* call(checkAndReload(action));
	});
}

function checkAndReload(action: ButtonAction): () => SagaGenerator<void> {
	return function* (): SagaGenerator<void> {
		const activityId = action.payload.activityId;
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		if (!activity) {
			throw new Error("Couldn't find activity");
		}

		const group = `downloadSignedPdf:${activity.descriptor.instance}`;

		const data = yield* select(ActivitySelectors.data(activityId));
		if (!SingleDocumentData.isInstance(data)) {
			yield* call(handleError, "Could not find document data in " + activity.id, group);
			return;
		}

		// Attachment-Pfad aus Button-Annotation lesen
		const annotations = yield* select(
			ModulfUiSelectors.selectButtonAnnotations(activityId, action.payload.engineEvent.payload.buttonPath)
		);

		let pathToFileAttachments = annotations?.find(
			(a: Annotation) => a.name === PATH_TO_FILE_ATTACHMENT_LOCATION
		)?.value;
		pathToFileAttachments = normalizePlatzhalterA12Path(pathToFileAttachments);
		if (!pathToFileAttachments) {
			yield* call(handleError, new Error("No pathToFileAttachmentLocation annotation given!"), group);
			return;
		}

		const isSaved = yield* call(saveActivity, activityId);
		if (!isSaved) {
			yield* call(handleError, "Could not save activity " + activityId, group);
			return;
		}

		try {
			const finalized: boolean = yield* call(getSignStatus, data.document.id, pathToFileAttachments);
			if (finalized) {
				yield* call(reloadActivity, activity);
				return;
			}
			// Signiervorgang noch nicht abgeschlossen — Hinweis anzeigen
			yield* put(
				createCustomNotificationAction({
					message: localizableFromLocaleResources(RESOURCE_KEYS.error.SIGN_STATUS_PENDING),
					icon: "info",
					severity: "info",
					activityId,
					group
				})
			);
		} catch (e) {
			if (isJsonRpc2ResponseArray(e)) {
				const rpcError = e.filter(JsonRpc2Response.error.isInstance).at(0)?.error;
				if (isRpcErrorResponse(rpcError) && rpcError.data.description.key === SIGN_TRANSACTION_EXPIRED_KEY) {
					// Das Download-Zeitfenster bei sign-me ist abgelaufen; der Server hat die
					// Signatur-Metadaten bereits zurückgesetzt. Aktivität neu laden, damit der
					// Anwender den Signiervorgang erneut starten kann.
					yield* put(
						createCustomNotificationAction({
							message: localizableFromLocaleResources(RESOURCE_KEYS.error.SIGN_TRANSACTION_EXPIRED),
							icon: "warning",
							severity: "warning",
							activityId,
							group
						})
					);
					yield* call(reloadActivity, activity);
					return;
				}
			}
			yield* call(handleError, e, group);
		}
	};
}

export function* handleError(error: any, group: string): SagaGenerator<void> {
	logger.error("Fehler beim Abrufen des signierten Dokuments", error);

	const errorMessage = localizableFromLocaleResources(RESOURCE_KEYS.error.DOWNLOAD_SIGNED_DOCUMENT_ERROR);
	yield* call(putErrorNotification, errorMessage, error, undefined, group);
}
